import { useEditor } from "@tiptap/react";
import { useCallback, useRef } from "react";

import { createTemplateEditorExtensions } from "../editor/extensions";
import {
  editorToHtml,
  editorToMessage,
  htmlToEditorContent,
  htmlToMessage,
  serializeEditorState,
} from "../editor/serialization";
import { TEMPLATE_TOKEN_NODE_NAME } from "../editor/TemplateTokenNode";
import { RICH_TEXT_CONTENT_CLASS } from "./richTextContentClass";
import type { TemplateEditorProps, TemplateEditorValue } from "./types";

/**
 * Owns the editing surfaces and keeps `value` coherent on every change. Rich text editing runs
 * through a TipTap editor; basic text and HTML editing run through a token-aware contentEditable
 * field. Token insertion is caret-aware in both: into the TipTap document as a chip node, or spliced
 * into the raw source.
 *
 * The editor seeds its content from `value.html` once at mount (the consumer mounts it after the
 * template has loaded / keys it by template identity to reset). The only in-session case where
 * stored HTML must flow back into the TipTap document is switching out of HTML mode, which the
 * caller handles by invoking {@link loadHtmlIntoEditor} from the tab-change event — so no effect
 * is needed. HTML-mode editing and token insertion are owned by the HTML token field, not here.
 */
export const useTemplateEditor = ({ value, onChange, disabled, preserveAnchorTags = false }: TemplateEditorProps) => {
  // Keep the latest value for use inside editor callbacks without stale closures.
  const valueRef = useRef(value);
  valueRef.current = value;

  const emit = useCallback(
    (partial: Partial<TemplateEditorValue>) => onChange({ ...valueRef.current, ...partial }),
    [onChange]
  );

  const editor = useEditor({
    editable: !disabled,
    extensions: createTemplateEditorExtensions(),
    editorProps: { attributes: { class: RICH_TEXT_CONTENT_CLASS } },
    content: htmlToEditorContent(value.html),
    onUpdate: ({ editor }) => {
      if (valueRef.current.mode !== "richtext") return;
      emit({
        html: editorToHtml(editor, { preserveAnchorTags }),
        message: editorToMessage(editor),
        editorState: serializeEditorState(),
      });
    },
  });

  // The HTML field owns whether its serialized `html` keeps literal anchors (preserveAnchorTags);
  // `message` is always derived link-aware, so a link's URL survives into plaintext regardless.
  const setHtml = useCallback(
    (html: string) => emit({ html, message: htmlToMessage(html), editorState: serializeEditorState() }),
    [emit]
  );

  // The basic text surface edits `message` directly. `html` is intentionally left untouched so a
  // later switch back to an HTML-capable channel restores the rich body; it is re-derived from
  // `message` at save time (in the web form), not here.
  const setMessage = useCallback((message: string) => emit({ message, editorState: serializeEditorState() }), [emit]);

  /** Load the current stored HTML into the TipTap document — call when entering a rich tab. */
  const loadHtmlIntoEditor = useCallback(() => {
    editor?.commands.setContent(htmlToEditorContent(valueRef.current.html), { emitUpdate: false });
  }, [editor]);

  // Inserts a chip into the TipTap document (Basic/Rich). HTML mode inserts via its own field.
  const insertToken = useCallback(
    (raw: string) => {
      editor?.chain().focus().insertContent({ type: TEMPLATE_TOKEN_NODE_NAME, attrs: { raw } }).run();
    },
    [editor]
  );

  return { editor, setHtml, setMessage, loadHtmlIntoEditor, insertToken };
};
